import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import { getCategoryBySlug } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;
  const category = article ? getCategoryBySlug(article.category) : undefined;
  const related = article ? getRelatedArticles(slug!, article.category) : [];

  useScrollToTop();

  const articleSchema = article
    ? {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        image: article.coverImage,
        author: {
          '@type': 'Organization',
          name: article.author,
        },
        datePublished: article.publishedAt,
        publisher: {
          '@type': 'Organization',
          name: '原神本子精选',
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `https://artsygen.github.io/article/${article.slug}`,
        },
      }
    : undefined;

  if (!article) {
    return (
      <>
        <SEOHead title="文章未找到" description="您访问的文章不存在。" />
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">文章未找到</h1>
            <p className="text-foreground-500 mb-6">您访问的文章不存在，可能已被移除或链接有误。</p>
            <Link to="/" className="px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://artsygen.github.io/article/${article.slug}`}
        ogImage={article.coverImage}
        ogType="article"
        schema={articleSchema || undefined}
      />
      <Navbar />
      <main className="min-h-screen pt-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              ...(category
                ? [{ label: category.name, href: `/category/${category.slug}` }]
                : []),
              { label: article.title },
            ]}
          />

          <div className="mt-6">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700 whitespace-nowrap">
                {article.character}
              </span>
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs text-foreground-500 whitespace-nowrap">
                  #{tag}
                </span>
              ))}
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 text-sm text-foreground-500 mb-8 flex-wrap">
              <span className="flex items-center gap-1 whitespace-nowrap">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-user-line text-xs"></i>
                </span>
                {article.author}
              </span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-line text-xs"></i>
                </span>
                {article.publishedAt}
              </span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-time-line text-xs"></i>
                </span>
                阅读时间 {article.readTime}
              </span>
            </div>
          </div>

          <div className="mb-10 rounded-lg overflow-hidden">
            <img
              src={article.coverImage}
              alt={article.title}
              title={`${article.title} - 原神本子精选`}
              className="w-full max-h-[500px] object-cover object-top"
            />
          </div>

          <div
            className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-12 pt-8 border-t border-background-200/70">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full text-xs bg-background-100 text-foreground-600 whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {related.length > 0 && (
          <section className="py-14 px-4 md:px-6 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {related.map((rel) => (
                  <ArticleCard key={rel.slug} article={rel} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}